// The one Collect Payment modal in the app. Rendered by OrderActionProvider
// AFTER the pipeline steps, so it always paints above the Check Out step it
// may have been opened from. Counter polish (2026-09-25):
//   - recording is unmistakable: a brief success state before the modal closes
//   - method must be chosen; Cash gets tendered/change; Card records only
//   - receipt is an explicit choice (SMS / none)
//   - opened from a deposit line (or on a Draft/Reserved order with an unpaid
//     booking deposit) it starts at the deposit, with the balance one tap away
import React from 'react';
import axios from '../../lib/axiosClient';
const defaultFmt = (n) => '$' + Number(n||0).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2});
const round2 = (n) => Math.round(Number(n || 0) * 100) / 100;

export default function CollectPaymentModal({ order: initialOrder, fmt = defaultFmt, onClose, onCollect, defaultAmount = null }) {
  const balanceOf = (o) => Math.max(0, round2(Number(o.total||0) - Number(o.amount_paid||0)));
  // Booking deposit still unpaid on a Draft/Reserved order → offer it first (same as a deposit line would).
  const depositLeftOf = (o) => {
    const due = o.next_action?.dialog?.deposit_due;
    if (!['draft', 'reserved'].includes(o.status) || due == null) return 0;
    return Math.max(0, round2(Number(due) - Number(o.amount_paid || 0)));
  };
  const startAmount = (o) => {
    const bal = balanceOf(o);
    const d = defaultAmount != null ? Number(defaultAmount) : (depositLeftOf(o) > 0 ? depositLeftOf(o) : bal);
    return Math.max(0, Math.min(d, bal)).toFixed(2);
  };
  // Always price the modal from a fresh copy of the order — the row or page
  // that opened it may be stale (BACKLOG item 15: a stale balance re-collected
  // three times).
  const [order, setOrder] = React.useState(initialOrder);
  React.useEffect(() => {
    let on = true;
    axios.get('/api/rentals/orders/' + initialOrder.id).then(r => {
      const o = r.data.order; if (!on || !o) return;
      setOrder(o);
      setAmount(startAmount(o));
    }).catch(() => {});
    return () => { on = false; };
  }, [initialOrder.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const [amount, setAmount] = React.useState(startAmount(initialOrder));
  const [method, setMethod] = React.useState(null);      // explicit choice required
  const [tendered, setTendered] = React.useState('');    // cash only
  const [note,   setNote]   = React.useState('');
  const [receipt,setReceipt]= React.useState(null);      // 'sms' | 'none' — explicit choice required
  const [saving, setSaving] = React.useState(false);
  const [done, setDone] = React.useState(null);          // { amount, method } → the brief success state
  const balance = balanceOf(order);
  const depositChip = defaultAmount != null ? Math.min(Number(defaultAmount), balance) : depositLeftOf(order);
  const overpay = Number(amount) > balance + 0.01;
  const amt = Number(amount);
  const change = method === 'cash' && tendered !== '' ? round2(Number(tendered) - amt) : null;
  const tenderShort = method === 'cash' && tendered !== '' && Number(tendered) + 0.005 < amt;
  const canSms = !!order.client_phone;
  const messaging = order.messaging?.mode;   // 'live' | 'redirected' | 'suppressed'
  const ready = amt > 0 && !!method && !!receipt && !tenderShort && !saving && !done;

  async function record() {
    setSaving(true);
    const parts = [note.trim()].filter(Boolean);
    if (method === 'cash' && tendered !== '') parts.push(`tendered ${fmt(tendered)}, change ${fmt(Math.max(0, change))}`);
    if (method === 'card') parts.push('card — recorded only, no charge');
    const ok = await onCollect(amount, method, parts.join(' · '), receipt === 'sms', overpay);
    setSaving(false);
    if (ok) {
      setDone({ amount: amt, method });
      setTimeout(() => onClose(), 2200);   // the success state is the confirmation; the host already refreshed the page beneath
    }
  }

  const S = {
    overlay: { position:'fixed',inset:0,background:'rgba(0,0,0,0.7)',zIndex:4100,display:'flex',alignItems:'center',justifyContent:'center',padding:20 },
    box:     { background:'#252838',border:'1px solid #1a1d2e',borderRadius:14,width:'100%',maxWidth:440,boxShadow:'0 20px 60px rgba(0,0,0,0.5)' },
    hdr:     { padding:'16px 20px',borderBottom:'1px solid #1a1d2e',display:'flex',justifyContent:'space-between',alignItems:'center' },
    body:    { padding:'20px' },
    lbl:     { fontSize:11,fontWeight:600,color:'#e2e8f0',textTransform:'uppercase',letterSpacing:'.4px',display:'block',marginBottom:5,marginTop:12 },
    inp:     { padding:'10px 12px',borderRadius:8,border:'1px solid #2a3050',background:'#181928',color:'#e2e8f0',fontSize:14,width:'100%',boxSizing:'border-box' },
    chip:    (on) => ({ padding:'6px 12px',borderRadius:6,border:`1px solid ${on?'#f97316':'#3a3f60'}`,background:on?'rgba(249,115,22,0.15)':'transparent',color:on?'#f97316':'#94a3b8',fontSize:11,cursor:'pointer',textTransform:'capitalize' }),
  };

  if (done) {
    return (
      <div style={S.overlay}>
        <div style={S.box} data-testid="collect-success" role="status">
          <div style={{ padding:'28px 20px', textAlign:'center' }}>
            <div style={{ fontSize:34, color:'#4ade80' }}>✓</div>
            <div style={{ fontSize:16, fontWeight:800, color:'#f1f5f9', marginTop:6 }}>Recorded {fmt(done.amount)} {done.method}</div>
            <div style={{ fontSize:12, color:'#94a3b8', marginTop:6 }}>Order #{order.id} · {order.client_name}{done.method === 'card' ? ' · no card was charged' : ''}</div>
            <div style={{ fontSize:11, color:'#64748b', marginTop:14 }}>You can undo this from the order page for the next 10 minutes.</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={S.overlay} onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={S.box} data-testid="collect-modal">
        <div style={S.hdr}>
          <div>
            <div style={{fontWeight:800,fontSize:15,color:'#e2e8f0'}}>💰 Collect Payment</div>
            <div style={{fontSize:11,color:'#e2e8f0',marginTop:2}}>Order #{order.id} · {order.client_name}</div>
          </div>
          <button onClick={onClose} aria-label="Close" style={{background:'none',border:'none',color:'#e2e8f0',cursor:'pointer',fontSize:18}}>✕</button>
        </div>
        <div style={S.body}>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:8,marginBottom:8}}>
            {[['Total',fmt(order.total),'#e2e8f0'],['Paid',fmt(order.amount_paid||0),'#4ade80'],['Balance',fmt(balance),'#f59e0b']].map(([l,v,c])=>(
              <div key={l} style={{background:'#181928',borderRadius:8,padding:'10px 8px',textAlign:'center'}}>
                <div style={{fontSize:10,color:'#e2e8f0',marginBottom:3}}>{l}</div>
                <div style={{fontSize:14,fontWeight:800,color:c}}>{v}</div>
              </div>
            ))}
          </div>
          <label style={S.lbl}>Amount Collecting</label>
          <input data-testid="collect-amount" style={{...S.inp,fontSize:20,fontWeight:800,color:overpay?'#f59e0b':'#4ade80',borderColor:overpay?'#f59e0b':'#3a3f60'}}
            type="number" min="0" step="0.01" inputMode="decimal" value={amount} onChange={e=>setAmount(e.target.value)} autoFocus />
          {overpay && <div data-testid="overpay-warning" style={{fontSize:11,color:'#f59e0b',marginTop:4}}>⚠️ {balance > 0 ? `Over the ${fmt(balance)} balance` : 'Nothing is due on this order'} — recording this is an overpayment the client is owed back</div>}
          <div style={{display:'flex',gap:4,marginTop:6,flexWrap:'wrap'}}>
            {depositChip > 0 && depositChip < balance && (
              <button type="button" data-testid="use-deposit" onClick={()=>setAmount(depositChip.toFixed(2))} style={{...S.chip(amt===depositChip),flex:2,textTransform:'none'}}>Deposit {fmt(depositChip)}</button>
            )}
            <button type="button" data-testid="use-balance" onClick={()=>setAmount(balance.toFixed(2))} style={{...S.chip(amt===balance),flex:2,textTransform:'none'}}>Balance {fmt(balance)}</button>
            {[25,50,75].map(p=>(
              <button key={p} type="button" onClick={()=>setAmount((balance*p/100).toFixed(2))} style={{...S.chip(false),flex:1}}>{p}%</button>
            ))}
          </div>

          <label style={S.lbl}>Payment Method {!method && <span style={{color:'#f59e0b',fontWeight:400,textTransform:'none'}}>— choose one</span>}</label>
          <div style={{display:'flex',gap:5,flexWrap:'wrap'}}>
            {['cash','card','check','venmo','zelle','other'].map(m=>(
              <button key={m} type="button" data-testid={`method-${m}`} style={S.chip(method===m)} onClick={()=>setMethod(m)}>{m}</button>
            ))}
          </div>
          {method === 'card' && <div data-testid="card-records-only" style={{fontSize:11,color:'#fbbf24',marginTop:6}}>Records only — no card is charged until Stripe Connect exists.</div>}
          {method === 'cash' && (
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginTop:8}}>
              <div>
                <div style={{fontSize:10,color:'#94a3b8',marginBottom:3}}>Cash tendered</div>
                <input data-testid="cash-tendered" style={{...S.inp,fontSize:16,fontWeight:700}} type="number" min="0" step="0.01" inputMode="decimal" value={tendered} onChange={e=>setTendered(e.target.value)} placeholder={amt.toFixed(2)} />
              </div>
              <div>
                <div style={{fontSize:10,color:'#94a3b8',marginBottom:3}}>Change</div>
                <div data-testid="cash-change" style={{...S.inp,fontSize:16,fontWeight:800,color:tenderShort?'#f87171':'#4ade80',background:'#1e2235'}}>{tendered === '' ? '—' : tenderShort ? `short ${fmt(amt - Number(tendered))}` : fmt(change)}</div>
              </div>
            </div>
          )}

          <label style={S.lbl}>Receipt {!receipt && <span style={{color:'#f59e0b',fontWeight:400,textTransform:'none'}}>— choose one</span>}</label>
          <div style={{display:'flex',gap:5,flexWrap:'wrap'}}>
            <button type="button" data-testid="receipt-sms" disabled={!canSms} title={canSms?'':'No phone on this order'} style={{...S.chip(receipt==='sms'),textTransform:'none',opacity:canSms?1:.5}} onClick={()=>setReceipt('sms')}>
              SMS receipt{canSms ? ` to ${order.client_phone}` : ' (no phone)'}{messaging === 'suppressed' ? ' — suppressed (parallel-run)' : messaging === 'redirected' ? ' — redirected (parallel-run)' : ''}
            </button>
            <button type="button" data-testid="receipt-none" style={{...S.chip(receipt==='none'),textTransform:'none'}} onClick={()=>setReceipt('none')}>No receipt</button>
          </div>

          <label style={S.lbl}>Note (optional)</label>
          <input style={S.inp} value={note} onChange={e=>setNote(e.target.value)} placeholder="e.g. paid at pickup" />

          <div style={{display:'flex',gap:10,marginTop:18}}>
            <button type="button" onClick={onClose} style={{flex:1,padding:'11px',borderRadius:8,border:'1px solid #2a3050',background:'transparent',color:'#e2e8f0',cursor:'pointer',fontSize:13}}>Cancel</button>
            <button type="button" data-testid="record-payment" disabled={!ready} onClick={record}
              title={!method?'Choose a payment method':!receipt?'Choose a receipt option':tenderShort?'Cash tendered is less than the amount':amt>0?'':'Enter an amount'}
              style={{flex:2,padding:'11px',borderRadius:8,border:'none',background:!ready?'#1e2235':overpay?'#451a03':'#14532d',color:!ready?'#64748b':overpay?'#fbbf24':'#4ade80',fontWeight:800,cursor:ready?'pointer':'not-allowed',fontSize:14}}>
              {saving?'Saving...':!method?'Choose a payment method':!(amt>0)?'Enter an amount':!receipt?'Choose a receipt option':tenderShort?'Cash tendered is short':overpay?`⚠️ Record overpayment ${fmt(amount)} ${method}`:`✓ Record ${fmt(amount)} ${method}${method==='card'?', no charge':''}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
